/**
 * PlatformAdminRoute: route guard for the platform-administration area.
 *
 * P7 wave 3a. Gates the `/platform-admin` area to users whose platform-wide
 * `User.role` is `'platformAdmin'`: the rare super-admin who manages the
 * platform itself (creating / editing / archiving hosted ecosystems). This is
 * the platform analogue of `EcosystemAdminRoute`, which gates on a per-tenant
 * `EcosystemMembership` role; the two are deliberately distinct concepts.
 *
 * Sourcing the role: `AuthContext` exposes the Firebase `user` but not the
 * MongoDB `User.role`. The role lives on the `/api/users/me` response (the
 * controller spreads the full User document, so `role` is present). This
 * guard fetches `/api/users/me` once on mount, rather than threading the
 * role through context.
 *
 * Loading-race handling (the P5-3a EcosystemAdminRoute lesson): a guard that
 * redirects before auth + the role lookup have resolved is a bug: it would
 * bounce a legitimate platform admin to the apex home on a hard refresh. So
 * resolution waits for BOTH `authLoading` to clear AND the role fetch to
 * settle before deciding; until then it renders a spinner.
 *
 * Resolution:
 *   - auth still loading, or the role fetch in flight -> centered spinner.
 *   - not signed in (or unverified)                   -> redirect to /login.
 *   - signed in but not a platform admin              -> redirect to apex `/`.
 *   - platform admin                                  -> render children.
 *
 * This client gate is a UX affordance only. The server endpoints enforce the
 * real boundary via `isPlatformAdmin`; a non-admin who bypasses this guard
 * still gets a 403.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';
import { Container, Spinner } from 'react-bootstrap';

import { useAuth } from '@/shared/contexts/AuthContext';
import { createAuthenticatedAxios } from '@/shared/services/axiosInstance';
import useRedirectToApex from '@/shared/hooks/useRedirectToApex';

export default function PlatformAdminRoute({ children }) {
    const { user, loading: authLoading } = useAuth();
    const location = useLocation();

    // null = role fetch not yet settled; the guard must NOT decide while
    // this is null (the loading-race guard).
    const [isPlatformAdmin, setIsPlatformAdmin] = useState(null);
    const isRedirectingToApex = useRedirectToApex('/platform-admin');

    useEffect(() => {
        let cancelled = false;

        const resolveRole = async () => {
            // No verified user: nothing to look up; mark the fetch settled
            // so the redirect-to-login branch can run.
            if (!user || !user.emailVerified) {
                if (!cancelled) {
                    setIsPlatformAdmin(false);
                }
                return;
            }
            try {
                const api = createAuthenticatedAxios(user);
                const response = await api.get('/api/users/me');
                if (!cancelled) {
                    setIsPlatformAdmin(response.data?.role === 'platformAdmin');
                }
            } catch {
                // A failed lookup is treated as "not an admin": fail closed.
                if (!cancelled) {
                    setIsPlatformAdmin(false);
                }
            }
        };

        // Re-run the lookup whenever the auth state settles or the user
        // changes. While auth is still loading, leave the role unresolved.
        if (!authLoading) {
            setIsPlatformAdmin(null);
            resolveRole();
        }

        return () => {
            cancelled = true;
        };
    }, [user, authLoading]);

    if (isRedirectingToApex) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" role="status">
                    <span className="visually-hidden">Loading…</span>
                </Spinner>
            </Container>
        );
    }

    // Wait for BOTH auth and the role fetch before deciding anything.
    if (authLoading || isPlatformAdmin === null) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" role="status">
                    <span className="visually-hidden">Loading…</span>
                </Spinner>
            </Container>
        );
    }

    // Not signed in (or unverified): send to login, preserving the target.
    if (!user || !user.emailVerified) {
        const redirectTo = location.pathname + location.search;
        return (
            <Navigate
                to={`/login?redirect=${encodeURIComponent(redirectTo)}`}
                replace
            />
        );
    }

    // Signed in but not a platform admin: bounce to the apex home.
    if (!isPlatformAdmin) {
        return <Navigate to="/" replace />;
    }

    return children;
}

PlatformAdminRoute.propTypes = {
    children: PropTypes.node.isRequired,
};
